import React, { useRef, useState, lazy } from 'react';
import PostHogHelper from '../../utils/posthog-helper';
import { navigate } from 'gatsby';
import { blindPostRequest } from '../../utils/network';

const Marketing = lazy(() => import('../forms/marketing'));
const Review = lazy(() => import('../forms/review'));
const Contact = lazy(() => import('../forms/contact'));
const General = lazy(() => import('../forms/general'));
const GetInTouch = lazy(() => import('../forms/get-in-touch'));
const Sales = lazy(() => import('../forms/sales'));
const ClaimYourGift = lazy(() => import('../forms/claim-gift'));
const Package = lazy(() => import('../forms/package'));
const Abuse = lazy(() => import('../forms/abuse'));

/**
 * I can't wait to make this file more dynamic
 * - rushing for christmas page
 * @returns react fragment with form fields
 */

const ContactFormWrapper = ({ block }) => {
  let contactType = 1; // 0 = contact, 1 = sales, 2 = abuse
  let contactUrl = 'https://api.bunny.net/contact/submit-form';
  /** Set by getWrappedForm  */
  let thankyouPageUrl = '';

  if (
    block._template === 'block__contact_form_review' ||
    block._template === 'block__contact_form_general' ||
    block._template === 'block__contact_form_get_in_touch' ||
    block._template === 'block__contact_form'
  ) {
    contactType = 0;
  }
  if (block._template === 'block__contact_form_abuse') {
    contactType = 2;
    contactUrl = 'https://api.bunny.net/contact/submit-form';
  }

  const randomIdPrefix =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);

  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const formRef = useRef(null);

  const [selectedCountry, setSelectedCountry] = useState('US');

  const handlePhoneCode = phoneCode => {
    setSelectedCountry(phoneCode);
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!formRef.current) {
      return;
    }

    var formData = new FormData(formRef.current);
    const contactEmail = `${formData.get('ContactEmail')}`;

    if (block.campaign && block.campaign.length > 0) {
      formData.append('Campaign', block.campaign);
    }

    if (formData.get('FullName') === null) {
      formData.append(
        'FullName',
        formData.get('FirstName') + ' ' + formData.get('LastName')
      );
      formData.delete('FirstName');
      formData.delete('LastName');
    }

    let phoneData;
    if (formData.get('Phone')) {
      phoneData = formData.get('Phone');
      formData.delete('Phone');
      formData.append('Phone', '+' + selectedCountry + ' ' + phoneData);
    }

    /**
     * Elements have any type for now, but we know they are HTMLFormControlsCollection.
     * @type {any}
     */
    const { elements } = formRef.current;

    if (elements && elements.Services) {
      /**
       * @type {Array<string>}
       */
      const servicesData = [];

      elements.Services.forEach(checkbox => {
        if (checkbox.checked) {
          servicesData.push(checkbox.value);
        }
      });

      const servicesString = servicesData.join(',');
      formData.set('Services', servicesString);
    }

    let Title = block._template;
    Title = Title.replace('block__contact_form', '');
    Title = Title.replaceAll('_', ' ');
    Title = Title.trim();
    if (Title.length <= 0) {
      Title = 'Contact';
    }
    Title = `${Title[0].toUpperCase()}${Title.slice(1)}`;
    formData.append('Title', Title);

    let formBody = {};
    for (var entry of formData.entries()) {
      formBody[entry[0]] = entry[1];
    }

    blindPostRequest(contactUrl, formBody).then(async responseOk => {
      setSubmitted(true);

      if (responseOk) {
        setSuccess(true);
        await PostHogHelper.promiseToCaptureEmail(contactEmail);
        navigate(thankyouPageUrl);
      } else {
        setSuccess(false);
      }
    });
  };

  // CSS classes
  if (block.form_style === 'Centered') {
    block.style_class = ' style-centered';
  } else {
    block.style_class = ' style-basic';
  }

  if (block._template === 'block__contact_form_get_in_touch') {
    block.cols_class = 'form-cols-git';
  } else {
    block.cols_class = 'form-cols';
  }

  const getWrappedForm = block_template => {
    switch (block_template) {
      case 'block__contact_form_review':
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <Review />;
      case 'block__contact_form':
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <Contact handlePhoneCode={handlePhoneCode} />;
      case 'block__contact_form_marketing':
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <Marketing handlePhoneCode={handlePhoneCode} />;
      case 'block__contact_form_get_in_touch':
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <GetInTouch />;
      case 'block__contact_form_contact_sales':
        thankyouPageUrl = 'https://bunny.net/thank-you-sales';
        return (
          <Sales
            handlePhoneCode={handlePhoneCode}
            randomIdPrefix={randomIdPrefix}
          />
        );
      case 'block__contact_form_claim_your_gift':
        thankyouPageUrl = 'https://bunny.net/thank-you-gift';
        return <ClaimYourGift handlePhoneCode={handlePhoneCode} />;
      case 'block__contact_form_package':
        thankyouPageUrl = 'https://bunny.net/thank-you-server';
        return <Package handlePhoneCode={handlePhoneCode} />;
      case 'block__contact_form_abuse':
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <Abuse />;
      case 'block__contact_form_general':
      default:
        thankyouPageUrl = 'https://bunny.net/thank-you';
        return <General />;
    }
  };

  return (
    <section
      className={`block ${block._template} ${block.css_class_name ? block.css_class_name : ''} contact-form ${block.add_form_margin ? 'form-margin' : ''} ${block.style_class ? block.style_class : ''}`}
    >
      <div className='container'>
        {block.image && (
          <img
            src={block.image}
            alt={block.image_alt_text}
            title={block.image_alt_text}
            className='bunny-no-touch'
          />
        )}
        <div className='row'>
          <div className={block.cols_class}>
            <div className='form-wr'>
              <form
                onSubmit={handleSubmit}
                ref={formRef}
              >
                {block._template === 'block__contact_form_marketing' && (
                  <FormImage block={block} />
                )}
                {submitted && !success && (
                  <div className='error-message'>
                    There was an error submitting this contact form. Please try
                    again or send us an email at sales@bunny.net.
                  </div>
                )}
                <div className={`row${success ? 'is-hidden' : ''}`}>
                  {getWrappedForm(block._template)}
                  <div className='col-12 form-submit'>
                    <input
                      type='hidden'
                      name='IsValid'
                      value='false'
                    />
                    <input
                      type='hidden'
                      name='Type'
                      value={contactType}
                    />
                    <div className='form-terms'>
                      <input
                        type='checkbox'
                        name='AgreeWithTerms'
                        required
                        value='I have read and agree to the bunny.net Privacy Policy.'
                      />
                      <label htmlFor='AgreeWithTerms'>
                        I have read and agree to the bunny.net{' '}
                        <a href='/privacy'>Privacy Policy.</a>
                      </label>
                    </div>

                    <button
                      type='submit'
                      name='submit'
                      className='form-action'
                      value={block.submit_label}
                    >
                      {block.submit_label}
                    </button>
                    <div
                      className='support-text'
                      dangerouslySetInnerHTML={{ __html: block.support_text }}
                    ></div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FormImage = ({ block }) => {
  return (
    <img
      src={block.side_image}
      alt={block.side_image_alt}
      title={block.side_image_alt}
      className='contact_form__image'
      loading='lazy'
    />
  );
};

export default ContactFormWrapper;
